import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Floating demo-invitation indicator. Two surfaces:
 *
 *   Desktop (≥768px)
 *     A pill bottom-right that morphs between "expanded" (full sentence +
 *     CTA at page top) and "collapsed" (compact label + CTA after scroll).
 *
 *   Mobile (<768px)
 *     A tiny circular "i" badge bottom-right. The expanded pill is too
 *     wide on phones — it overlaps the hero date and squashes the editorial
 *     composition. Tapping the badge opens a small sheet above it with the
 *     full message and CTA; tapping outside (or the link itself) dismisses.
 *
 * Rendered only when `/public` returned `isDemo: true` for the reserved
 * `demo-heirloom` slug (see api/constants/demoEvent.mjs).
 *
 * Why bottom-right (always) rather than a top banner? The Webflow navbar
 * is `position: fixed; z-index: 2000` with a 90vw white inner container,
 * so any top-anchored ribbon collides with it.
 *
 * @param {{ show?: boolean }} props
 *   `show` — pass the parent's "page is ready" signal (e.g. `showContent`
 *   from RSVP.jsx). When false the badge is rendered but invisible, so
 *   it doesn't flash through the loading overlay.
 */
const SCROLL_COLLAPSE_PX = 80;
const MOBILE_QUERY = "(max-width: 767px)";
// Single timing curve for every property that morphs — keeps the
// expand/collapse feeling like one coordinated motion rather than several
// independent transitions firing at different speeds.
const MORPH_TRANSITION = [
  "padding 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  "gap 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  "font-size 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  "max-width 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  "border-radius 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  "box-shadow 420ms cubic-bezier(0.22, 1, 0.36, 1)",
  "opacity 320ms ease",
].join(", ");

const DemoRibbon = ({ show = true }) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches,
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const wrapperRef = useRef(null);
  const createOwnUrl = `${import.meta.env.VITE_APP_URL || ""}/couple/create-event?plan=heirloom`;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onScroll = () => {
      setCollapsed(window.scrollY > SCROLL_COLLAPSE_PX);
    };
    // Seed once so a refresh part-way down the page lands in the right state.
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => {
      setIsMobile(e.matches);
      // Closing the sheet on resize-to-desktop avoids it being stuck open
      // off-screen when the breakpoint switches mid-interaction.
      if (!e.matches) setMobileOpen(false);
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onPointerDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setMobileOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  if (isMobile) {
    return (
      <div
        ref={wrapperRef}
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          zIndex: 9999,
          opacity: show ? 1 : 0,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 320ms ease",
        }}
      >
        {/* Sheet — anchored above the badge, slides/fades in on open */}
        <div
          role="region"
          aria-label={t("demoRibbon.ariaLabel")}
          aria-hidden={!mobileOpen}
          style={{
            position: "absolute",
            right: 0,
            bottom: "calc(100% + 10px)",
            width: "min(78vw, 320px)",
            background: "rgba(0, 0, 0, 0.92)",
            color: "rgba(255, 255, 255, 0.95)",
            padding: "14px 16px",
            borderRadius: 14,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.28)",
            fontSize: 13,
            lineHeight: 1.4,
            letterSpacing: "0.02em",
            opacity: mobileOpen ? 1 : 0,
            transform: mobileOpen
              ? "translateY(0) scale(1)"
              : "translateY(6px) scale(0.96)",
            transformOrigin: "bottom right",
            transition:
              "opacity 220ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
            pointerEvents: mobileOpen ? "auto" : "none",
          }}
        >
          <div style={{ opacity: 0.9, marginBottom: 10 }}>
            {t("demoRibbon.label")}
          </div>
          <a
            href={createOwnUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block",
              color: "rgba(255, 255, 255, 0.98)",
              fontWeight: 500,
              textDecoration: "underline",
              textUnderlineOffset: 3,
              letterSpacing: "0.04em",
            }}
          >
            {t("demoRibbon.cta")}
          </a>
        </div>

        {/* Badge — tiny circular trigger */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={t("demoRibbon.ariaLabel")}
          aria-expanded={mobileOpen}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "none",
            background: "rgba(0, 0, 0, 0.92)",
            color: "rgba(255, 255, 255, 0.95)",
            boxShadow: "0 8px 22px rgba(0, 0, 0, 0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            fontFamily: "Georgia, 'Palatino Linotype', serif",
            fontSize: 18,
            fontStyle: "italic",
            lineHeight: 1,
          }}
        >
          i
        </button>
      </div>
    );
  }

  return (
    <a
      href={createOwnUrl}
      target="_blank"
      rel="noopener noreferrer"
      role="region"
      aria-label={t("demoRibbon.ariaLabel")}
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 9999,
        display: "inline-flex",
        alignItems: "center",
        gap: collapsed ? 10 : 14,
        background: "rgba(0, 0, 0, 0.92)",
        color: "rgba(255, 255, 255, 0.95)",
        padding: collapsed ? "10px 16px" : "14px 22px",
        borderRadius: 999,
        fontSize: collapsed ? 12 : 13,
        letterSpacing: "0.04em",
        textDecoration: "none",
        // Max-width morphs so the label can wrap into two lines on small
        // viewports without snapping the pill's width on toggle.
        maxWidth: collapsed ? 320 : 440,
        boxShadow: collapsed
          ? "0 6px 24px rgba(0, 0, 0, 0.18)"
          : "0 10px 32px rgba(0, 0, 0, 0.22)",
        opacity: show ? 1 : 0,
        transition: MORPH_TRANSITION,
        pointerEvents: show ? "auto" : "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.6)",
          flexShrink: 0,
        }}
      />
      <span style={{ opacity: 0.85, lineHeight: 1.35 }}>
        {collapsed ? t("demoRibbon.collapsedLabel") : t("demoRibbon.label")}
      </span>
      <span
        style={{
          opacity: 0.95,
          fontWeight: 500,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {t("demoRibbon.cta")}
      </span>
    </a>
  );
};

export default DemoRibbon;
